import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, } from "react-router-dom";
import socket from "../../socket";
import api from "../../api/api";

const detectHasLocation = (user, allowedCities = []) => {
  if (!user) return false;

  const getCity = (u) => {
    return u?.location?.city || u?.city || null;
  };

  const userCity = getCity(user);
  if (!userCity) return false;

  const normalizedCity = userCity.toString().toLowerCase().trim();

  // If allowedCities is empty → accept any city (or change to false)
  if (allowedCities.length === 0) {
    return true;
  }

  return allowedCities.includes(normalizedCity);
};

const calculateVendorEarnings = (order) => {
  if (!order?.items?.length) return 0;

  return order.items.reduce((sum, item) => {
    const price = Number(item.basePrice ?? item.pricePerUnit ?? item.price ?? 0);
    const qty   = Number(item.quantity ?? 0);
    return sum + price * qty;
  }, 0);
};

export const Dashboard = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userId, setUserId] = useState(null);
  const [greeting, setGreeting] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [locationChecked, setLocationChecked] = useState(false);
  const [showLocationUpdatePopup, setShowLocationUpdatePopup] = useState(false);
  const [allowedCitiesList, setAllowedCitiesList] = useState([]);
  
  const [loading, setLoading] = useState(false);

  // Dashboard data
  const [metrics, setMetrics] = useState({
    revenueToday: 0,
    pendingOrders: 0,
    dispatchedOrders: 0,
    cancelledOrders: 0,
    topProducts: [],
    recentOrders: []
  });


  // -------------------------------------------
  // Load Token
  // -------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setUserId(payload.userId);
      } catch(err) {
        console.log(err)
      }
    }
  }, []);

  // -------------------------------------------
  // Socket listener
  // -------------------------------------------
  useEffect(() => {
    if (!userId) return;

    socket.emit("join", userId);

    socket.on("orderUpdate", (data) => {
      setNotifications((prev) => [data, ...prev]);
    });

    return () => socket.off("orderUpdate");
  }, [userId]);

  // -------------------------------------------
  // Dashboard Data Loader
  // -------------------------------------------
  useEffect(() => {
  const loadDashboardData = async () => {
    try {
      const orderRes = await api.orders.vendorMine();

      if (!orderRes || !Array.isArray(orderRes.orders)) return;

      const orders = orderRes.orders;

      // Helper: is today
      const isToday = (dateStr) => {
        const d = new Date(dateStr);
        const now = new Date();
        return (
          d.getFullYear() === now.getFullYear() &&
          d.getMonth() === now.getMonth() &&
          d.getDate() === now.getDate()
        );
      };

      // ── Revenue Today ── corrected
      const revenueToday = orders
        .filter((o) => o.paymentStatus === "paid" && isToday(o.createdAt))
        .reduce((sum, order) => {
          if (!Array.isArray(order.items)) return sum;

          const orderVendorEarnings = order.items.reduce((acc, item) => {
            const price = Number(item.basePrice ?? item.pricePerUnit ?? item.price ?? 0);
            const qty   = Number(item.quantity ?? 0);
            return acc + price * qty;
          }, 0);

          return sum + orderVendorEarnings;
        }, 0);

      // 2️⃣ Pending Count
      const pendingOrders = orders.filter((o) => o.status === "pending").length;

      // 3️⃣ Dispatched / Completed
      const dispatchedOrders = orders.filter(
        (o) => o.status === "delivered" || o.status === "shipped"
      ).length;

      // 4️⃣ Cancelled
      const cancelledOrders = orders.filter((o) => o.status === "cancelled").length;

      // 5️⃣ Top Products (already mostly good – just make price fallback safer)
      const productSalesMap = {};

      orders.forEach((order) => {
        if (!Array.isArray(order.items)) return;

        order.items.forEach((item) => {
          if (!item?.productId?._id) return;

          const id = item.productId._id;

          if (!productSalesMap[id]) {
            productSalesMap[id] = {
              name: item.productId.name || item.name || "Unknown product",
              img: item.productId.images?.[0] || null,
              // Prefer basePrice for display consistency
              price: Number(item.basePrice ?? item.pricePerUnit ?? item.price ?? 0),
              sold: 0,
            };
          }

          productSalesMap[id].sold += Number(item.quantity || 0);
        });
      });

      const topProducts = Object.values(productSalesMap)
        .sort((a, b) => b.sold - a.sold)
        .slice(0, 3);

      // 6️⃣ Recent Orders – unchanged
      const recentOrders = [...orders]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);

      setMetrics({
        revenueToday,
        pendingOrders,
        dispatchedOrders,
        cancelledOrders,
        topProducts,
        recentOrders,
      });
    } catch (err) {
      console.log("Error loading dashboard:", err);
    }
  };

  loadDashboardData();
}, []);

// =============================================
//  LOCATION LOGIC
// =============================================

useEffect(() => {
  const initializeLocation = async () => {
    try {
      setLoading(true);

      const [profileRes, allowedRes] = await Promise.all([
        api.profile.viewProfile(),
        api.location.allowedCities()
      ]);

      const user = profileRes?.user || profileRes;
      if (!user) {
        localStorage.setItem("hasLocation", "false");
        setUserProfile(null);
        setLocationChecked(true);
        setShowLocationUpdatePopup(true);
        return;
      }

      const allowedCitiesRaw = allowedRes?.data?.map(item => 
        item.city?.trim()
      ).filter(Boolean) || [];

      const allowedCities = allowedCitiesRaw.map(city => city.toLowerCase().trim());

      const hasValidLocation = detectHasLocation(user, allowedCities);

      setUserProfile(user);
      localStorage.setItem("hasLocation", hasValidLocation ? "true" : "false");

      setAllowedCitiesList(allowedCitiesRaw);

      setLocationChecked(true);
      setShowLocationUpdatePopup(!hasValidLocation);

    } catch (err) {
      console.error("Failed to check location:", err);
      localStorage.setItem("hasLocation", "false");
      setLocationChecked(true);
      setShowLocationUpdatePopup(true);
    } finally {
      setLoading(false);
    }
  };

  initializeLocation();
}, []);




  useEffect(() => {
  const hour = new Date().getHours();

  if (hour < 12) {
    setGreeting("Good morning");
  } else if (hour < 17) {
    setGreeting("Good afternoon");
  } else {
    setGreeting("Good evening");
  }
}, []);


  return (
    <div className="min-h-screen bg-[#fff8e1]">
    {locationChecked && showLocationUpdatePopup && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
        <div className="bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl text-center">
          
          <div className="mx-auto w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
            📍
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            Delivery Area Issue
          </h3>

          {/* Current Location */}
          {(() => {
            const currentCity = userProfile?.location?.city || userProfile?.city;
            return currentCity ? (
              <div className="mb-6">
                <p className="text-gray-600 text-sm">Your current location is:</p>
                <p className="font-semibold text-orange-600 text-xl mt-1">
                  {currentCity}
                </p>
                <p className="text-red-600 text-sm mt-2 font-medium">
                  This area is not yet supported for delivery.
                </p>
              </div>
            ) : (
              <p className="text-gray-600 mb-6">
                You have not set your delivery location yet.
              </p>
            );
          })()}

          {/* Allowed Cities */}
          <div className="bg-gray-50 rounded-2xl p-5 mb-8 text-left">
            <p className="font-semibold text-gray-700 mb-3">
              ✅ Currently supported locations:
            </p>
            
            {allowedCitiesList.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {allowedCitiesList.map((city, index) => (
                  <span
                    key={index}
                    className="inline-block bg-white px-4 py-2 rounded-xl text-sm border border-gray-200 shadow-sm"
                  >
                    {city}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">No supported cities available yet.</p>
            )}
          </div>

          <p className="text-gray-600 mb-8 leading-relaxed">
            Please update your location to one of the supported areas above to begin receiving orders.
          </p>

          <button
            onClick={() => navigate("/update-location")}
            className="w-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold py-4 rounded-2xl text-lg transition"
          >
            Update My Location Now
          </button>

          <button
            onClick={() => setShowLocationUpdatePopup(false)}
            className="mt-5 text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            I'll do it later
          </button>
        </div>
      </div>
    )}
      <div
        className="
          mx-auto w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16 pt-10 md:pt-12 xl:pt-16 pb-32 md:pb-40
          [font-family:'Nunito',Helvetica] text-[#5d4037]
          pb-32 md:pb-40
        "
      >
    
        <div className="pt-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img alt="avatar" className="w-9 h-9 rounded-full object-cover"
            src="https://cdn-icons-png.flaticon.com/512/149/149071.png"/>
            <div>
              <p className="font-extrabold leading-tight text-[15px] md:text-base">
                {greeting}
              </p>
              <p className="text-[12px] md:text-[13px] leading-tight">
                Let’s make sales today!
              </p>
            </div>
          </div>

          {/* Notification Bell */}
          <div
            onClick={() => setShowDropdown((prev) => !prev)}
            className="relative w-9 h-9 rounded-full bg-[#eadfb7] grid place-items-center cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
              viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
              <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            {notifications.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#c2452d] border-2 border-[#fff8e1]" />
            )}
          </div>

          {/* Notification Dropdown */}
          {showDropdown && (
            <div className="absolute top-12 right-0 w-72 bg-white border border-[#eadfb7] rounded-lg shadow-lg z-10">
              <div className="p-2 max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-center text-sm text-gray-500 py-4">No notifications</p>
                ) : (
                  notifications.map((n, i) => (
                    <div key={i} className="p-2 border-b border-[#eadfb7]">
                      <p className="font-semibold text-[13px]">{n.message}</p>
                      <p className="text-xs text-gray-500">Order ID: {n.orderId}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
  
        {/* --------------------- METRICS --------------------- */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 xl:gap-6">
          <div className="rounded-[12px] bg-[#f7c945] p-4">
            <p className="text-[12px] md:text-[13px] font-extrabold">REVENUE TODAY</p>
            <p className="mt-1 font-extrabold text-[18px] md:text-[20px]">
              ₦{metrics.revenueToday.toLocaleString()}
            </p>
          </div>

          <div className="rounded-[12px] bg-white p-4 border border-[#eadfb7]">
            <p className="text-[12px] font-extrabold">PENDING ORDERS</p>
            <p className="mt-1 text-[14px] md:text-[15px]">• {metrics.pendingOrders}</p>
          </div>

          <div className="rounded-[12px] bg-white p-4 border border-[#eadfb7]">
            <p className="text-[12px] font-extrabold">DISPATCHED</p>
            <p className="mt-1 font-extrabold">{metrics.dispatchedOrders}</p>
          </div>

          <div className="rounded-[12px] bg-white p-4 border border-[#eadfb7]">
            <p className="text-[12px] font-extrabold">CANCELLED</p>
            <p className="mt-1 font-extrabold">{metrics.cancelledOrders}</p>
          </div>
        </div>

       
        <p className="mt-8 font-extrabold">Quick Actions</p>
        <div className="mt-4 flex items-start justify-between gap-6 xl:gap-12">

          <button type="button" onClick={() => navigate("/products/new")} className="flex flex-col items-center w-[110px] md:w-[120px]">
            <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </div>
            <span className="mt-2 text-[13px] md:text-[14px]">Add products</span>
          </button>

          <button type="button" onClick={() => navigate("/orders")} className="flex flex-col items-center w-[110px] md:w-[120px]">
            <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                <rect x="3" y="4" width="18" height="14" rx="2"/><path d="M7 8h10M7 12h6"/>
              </svg>
            </div>
            <span className="mt-2 text-[13px] md:text-[14px]">View Order</span>
          </button>

          {/* <button type="button" onClick={() => navigate("/analytics")} className="flex flex-col items-center w-[110px] md:w-[120px]">
            <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                <path d="M3 12h4v8H3zM10 8h4v12h-4zM17 4h4v16h-4z"/>
              </svg>
            </div>
            <span className="mt-2 text-[13px] md:text-[14px]">Analytics</span>
          </button> */}

          <button type="button" onClick={() => navigate("/withdraw")} className="flex flex-col items-center w-[110px] md:w-[120px]">
          <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
          <path d="M12 8v8M8 12h8" />
          <rect x="3" y="4" width="18" height="16" rx="2" />
          </svg>
          </div>
          <span className="mt-2 text-[13px] md:text-[14px]">Withdraw</span>
          </button>

        </div>

        {/* --------------------- TOP PRODUCTS --------------------- */}
        <p className="mt-8 font-extrabold">Top Products</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {metrics.topProducts.map((p, i) => (
            <div key={i} className="rounded-[10px] bg-[#ede6c9] px-2 py-2 flex items-center">
              <img alt="" src={p.img} className="w-12 h-12 rounded-[8px] object-cover" />
              <div className="ml-3">
                <p className="font-bold text-[14px] md:text-[15px]">{p.name}</p>
                <p className="text-[12px] md:text-[13px]">₦{p.price.toLocaleString()}</p>
              </div>

              <div className="ml-auto text-right pr-1">
                <p className="font-bold">{p.sold}</p>
                <p className="text-[12px]">Sold</p>
              </div>
            </div>
          ))}

          {metrics.topProducts.length === 0 && (
            <p className="text-gray-500 text-sm">No product sales yet.</p>
          )}
        </div>

        <p className="mt-8 font-extrabold">Recent Orders</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {metrics.recentOrders.map((o, i) => (
            <div key={i} className="relative rounded-[12px] bg-[#eadfb7] p-3 flex overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#c2452d]" />
              <div className="flex-1 pl-3">
                <div className="flex items-center justify-between">
                  <p className="font-bold">Order #{o.orderId}</p>
                  <p className="font-bold">
                    ₦{o.items?.reduce((sum, item) => 
    sum + Number(item.basePrice ?? item.pricePerUnit ?? 0) * Number(item.quantity ?? 0), 
    0).toLocaleString() || '0'}
                  </p>
                </div>

                <p className="text-[12px] md:text-[13px] mt-1">
                  {o.buyerPhone} • {o.items.length} items
                </p>

                <span
  className={`
    inline-block mt-2 text-[12px] px-2 py-1 rounded-full
    ${
      o.status === "pending"
        ? "bg-yellow-100 text-yellow-700"
        : o.status === "delivered"
        ? "bg-green-100 text-green-700"
        : o.status === "cancelled"
        ? "bg-red-100 text-red-700"
        : "bg-gray-100 text-gray-700"
    }
  `}
>
  {o.status}
</span>
              </div>
              </div>
          ))}
         
        </div>
      </div>

      <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
        <div className="px-6 pt-2 pb-4 bg-[#fff8e1]">
          <div className="grid grid-cols-4 gap-10 text-[12px] md:text-[13px] text-[#5d4037]">
       
            <div className="flex flex-col items-center">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M3 10.5L12 4l9 6.5" /><path d="M5 10v10h14V10" />
                </svg>
              </div>
              <span className="mt-1 font-semibold">Overview</span>
            </div>
            <button 
              type="button" 
              onClick={() => navigate("/orders")} 
              className="flex flex-col items-center" 
            > 
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                </svg>
              </div>
              <span className="mt-1 text-[#5d4037]/80">Orders</span>

            </button>
            <button 
              type="button" 
              onClick={() => navigate("/products")} 
              className="flex flex-col items-center" 
            > 
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                </svg>
              </div>
              <span className="mt-1 text-[#5d4037]/80">Product</span>

            </button>
            <button 
              type="button" 
              onClick={() => navigate("/profile")} 
              className="flex flex-col items-center" 
            > 
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                </svg>
              </div>
              <span className="mt-1 text-[#5d4037]/80">Profile</span>

            </button>
          </div>
        </div>
      
        <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
          <div className="w-[120px] h-[6px] rounded-full bg-white" />
        </div>
      </div>
    </div>
  );
};
